import type { LeDataContentBlock } from '~/types/leData';

import { normalizeTableBlock } from '~/utils/leData/tableBlock';

import { isExecutableLensSql } from './lensSql';

function pickText(payload: Record<string, unknown>): string {
  for (const key of ['markdown', 'text', 'content', 'message', 'body']) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return '';
}

/** Preserve intent and evidence for planning without exposing the planner instruction as intent. */
export function dashboardBriefFromAnswer(blocks: Array<LeDataContentBlock>, sourceQuestion?: string): string {
  const recommendation = blocks
    .filter((block) => block.type === 'text')
    .map((block) => pickText(block.payload).trim())
    .filter(Boolean)
    .join('\n\n');
  const resultDescriptions = blocks
    .filter((block) => block.type === 'chart' || block.type === 'table')
    .map((block) => {
      if (block.type === 'table') {
        const table = normalizeTableBlock(block.payload);
        return `Table: ${table.headers.join(', ') || 'result included in this answer'} (${table.totalRowCount} rows)`;
      }
      const labels = ['title', 'label', 'description', 'chartType', 'xKey', 'yKey', 'categoryField', 'valueField']
        .map((key) => (typeof block.payload[key] === 'string' ? `${key}: ${block.payload[key]}` : ''))
        .filter(Boolean);
      return `Chart: ${labels.join(', ') || 'result included in this answer'}`;
    })
    .join('\n');
  const sqlEvidence = blocks
    .filter((block) => block.type === 'sql')
    .map((block) => {
      for (const key of ['sql', 'content', 'query', 'statement', 'body']) {
        const value = block.payload[key];
        if (typeof value === 'string' && isExecutableLensSql(value)) {
          return value.trim();
        }
      }
      return '';
    })
    .filter(Boolean)
    .join('\n\n');

  return [
    'Prepare the final user-facing results from this answer for review before creating anything. Select the results the narrative supports; do not treat every exploratory query as a final result. Retain the stated filters and date scope.',
    'Build data cards with executable, validated query bindings. Use the governed metric definitions from the source context; do not change their numerator, denominator or classification during transfer. Query and validate any requested calculated result before saving it. Do not create empty or unconnected data-card placeholders. If evidence cannot support a requested result, explain that specific limitation in the insight rather than adding a card that needs setup.',
    sourceQuestion?.trim() ? `Original user question:\n${sourceQuestion.trim()}` : '',
    recommendation ? `Answer narrative:\n${recommendation}` : '',
    resultDescriptions ? `Chart and table context:\n${resultDescriptions}` : '',
    sqlEvidence
      ? `Executed SQL evidence (context only: use it to retain filters and date scope, not to infer extra final results):\n${sqlEvidence}`
      : '',
  ]
    .filter(Boolean)
    .join('\n\n');
}
